// Converts a viewport-space DOMRect into the editor host node's content
// coordinate space. Overlays (highlight menu, quick edit menu) are absolutely
// positioned inside #editor (position: relative), so coordinates relative to
// #editor's box are correct regardless of page scroll or the focus-mode
// scroll container — the host rect moves with the content.
function toEditorContentCoords(rect, hostNode) {
  const hostRect = hostNode.getBoundingClientRect();
  return {
    left: rect.left - hostRect.left,
    top: rect.top - hostRect.top,
    right: rect.right - hostRect.left,
    bottom: rect.bottom - hostRect.top,
  };
}

function getEditorContentCoordsForRange(view, hostNode, from, to, { suppressStaleRange = false } = {}) {
  const start = view.domAtPos(from);
  const end = view.domAtPos(to);
  const range = document.createRange();

  try {
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
  } catch (error) {
    if (suppressStaleRange && error?.name === "IndexSizeError") {
      return null;
    }

    throw error;
  }

  return toEditorContentCoords(range.getBoundingClientRect(), hostNode);
}

// Given a Prose Mirror editor and the #editor host node
// return the pixel position of the selected text in the host's content space
export function getSelectedTextPixelPosition(editor, hostNode) {
  const { selection } = editor.state;
  const { from, to } = selection;

  // Ensure that the selection is non-empty
  if (from === to) return null;

  const view = editor.view;
  return getEditorContentCoordsForRange(view, hostNode, from, to, { suppressStaleRange: true });
}

export const getPositionOfRange = (editor, hostNode, from, to) => {
  const view = editor.view;
  return getEditorContentCoordsForRange(view, hostNode, from, to);
};

// Converts an arbitrary element's bounding rect into the same content space
// as getSelectedTextPixelPosition, so overlay clipping comparisons stay valid.
export function getElementContentRect(element, hostNode) {
  return toEditorContentCoords(element.getBoundingClientRect(), hostNode);
}

// Given a PM editor returns the paragraph containing the selected text
export function getSelectedParagraph(editor, range) {
  const { state } = editor.view;
  const { selection } = state;

  if (range && typeof range.index === "number") {
    const parent = state.doc.resolve(range.index).parent;

    return parent.content.content.map((node) => node.text).join("");
  }

  if (selection.empty) {
    throw new Error("Cannot get paragraph from empty selection");
  }

  const parent = editor.view.state.selection.$from.parent;

  return parent.content.content.map((node) => node.text).join("");
}

// Replaces the current selection, or an explicitly captured range, with text.
export function replaceSelection(editor, text, range) {
  const { state } = editor.view;
  const { selection, schema } = state;
  const from = typeof range?.index === "number" ? range.index : selection.from;
  const to = typeof range?.index === "number" && typeof range?.length === "number" ? range.index + range.length : selection.to;

  // Create a text node with the given text using the schema
  const textNode = schema.text(text);

  // Create a new transaction to replace the current selection with the new text node
  const transaction = state.tr.replaceWith(from, to, textNode);

  // Apply the transaction to the editor state, updating the editor view
  editor.view.dispatch(transaction);
}

export function splitSingleNewlines(input) {
  return input.replace(/\n+/g, '\n').split('\n');
}

const pendingStreamChunkBoundaries = new WeakSet();

export function clearPendingStreamChunkBoundary(streamKey) {
  if (streamKey) {
    pendingStreamChunkBoundaries.delete(streamKey);
  }
}

// A newline at the end of a transport chunk may be the first half of a
// paragraph boundary split across chunks. Defer it instead of inserting an
// empty paragraph; the next chunk consumes it unless that chunk already starts
// with a newline. Weak keys keep the two comparison editors independent.
export function splitStreamChunkLines(input, streamKey) {
  const lines = splitSingleNewlines(input);
  const hasTrailingBoundary = lines[lines.length - 1] === "";

  if (hasTrailingBoundary) {
    lines.pop();
  }

  if (streamKey && pendingStreamChunkBoundaries.has(streamKey) && lines[0] !== "") {
    lines.unshift("");
  }

  if (streamKey) {
    if (hasTrailingBoundary) {
      pendingStreamChunkBoundaries.add(streamKey);
    } else {
      pendingStreamChunkBoundaries.delete(streamKey);
    }
  }

  return lines;
}
